import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../Api/api";

// ─── Color tokens (matches Dashboard + mobile app) ─────────────────────
const C = {
  navy1: "#0F172A", navy2: "#1E293B", navy3: "#334155",
  cyan: "#06B6D4", cyanD: "#0891B2", cyanBg: "#F0F9FF", cyanBorder: "#BAE6FD",
  bg: "#F8FAFC", card: "#FFFFFF", border: "#E5E7EB",
  text1: "#111827", text2: "#374151", text3: "#6B7280", text4: "#9CA3AF",
  green: "#10B981", greenBg: "#D1FAE5",
  amber: "#F59E0B", amberBg: "#FEF3C7",
  red: "#EF4444", redBg: "#FEE2E2",
};

// ─── Icons ─────────────────────────────────────────────────────────────
const Svg = ({ children, size = 18, color = "currentColor", fill = "none", sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconBack    = ({ size, color }) => <Svg size={size} color={color}><polyline points="15 18 9 12 15 6"/></Svg>;
const IconPlus    = ({ size, color }) => <Svg size={size} color={color}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Svg>;
const IconClose   = ({ size, color }) => <Svg size={size} color={color}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Svg>;
const IconCalendar= ({ size, color }) => <Svg size={size} color={color}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Svg>;
const IconCheck   = ({ size, color }) => <Svg size={size} color={color}><polyline points="20 6 9 17 4 12"/></Svg>;
const IconSend    = ({ size, color }) => <Svg size={size} color={color}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></Svg>;
const IconChevL   = ({ size, color }) => <Svg size={size} color={color}><polyline points="15 18 9 12 15 6"/></Svg>;
const IconChevR   = ({ size, color }) => <Svg size={size} color={color}><polyline points="9 18 15 12 9 6"/></Svg>;
const IconInfo    = ({ size, color }) => <Svg size={size} color={color} fill={color} sw={0}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></Svg>;
const IconClock   = ({ size, color }) => <Svg size={size} color={color}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Svg>;
const IconX       = ({ size, color }) => <Svg size={size} color={color} fill={color} sw={0}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/></Svg>;
const IconEmpty   = ({ size, color }) => <Svg size={size} color={color}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Svg>;

// ─── Date Picker Modal ─────────────────────────────────────────────────
function DatePickerModal({ visible, onClose, onSelect, currentDate, title }) {
  const init = currentDate ? new Date(currentDate + "T00:00:00") : new Date();
  const [viewDate, setViewDate] = useState(init);
  const [picked,   setPicked]   = useState(init);

  useEffect(() => {
    if (visible) {
      const d = currentDate ? new Date(currentDate + "T00:00:00") : new Date();
      setViewDate(d); setPicked(d);
    }
  }, [visible, currentDate]);

  const daysInMonth  = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstWeekday = (y, m) => new Date(y, m, 1).getDay();

  const buildGrid = () => {
    const y = viewDate.getFullYear(), m = viewDate.getMonth();
    const cells = Array(firstWeekday(y, m)).fill(null);
    for (let d = 1; d <= daysInMonth(y, m); d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };

  const isSel = (d) => d && d.getDate() === picked.getDate() && d.getMonth() === picked.getMonth() && d.getFullYear() === picked.getFullYear();
  const isToday = (d) => { const n = new Date(); return d && d.getDate() === n.getDate() && d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear(); };

  const confirm = () => {
    const y = picked.getFullYear(), m = String(picked.getMonth()+1).padStart(2,"0"), d = String(picked.getDate()).padStart(2,"0");
    onSelect(`${y}-${m}-${d}`); onClose();
  };

  const monthLabel  = viewDate.toLocaleDateString("en-US", { month:"long", year:"numeric" });
  const pickedLabel = picked.toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" });
  const grid = buildGrid();

  if (!visible) return null;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, display:"flex", flexDirection:"column" }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.5)" }} />

      {/* Sheet slides up from bottom */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0,
        background:"#fff", borderRadius:"28px 28px 0 0",
        maxHeight:"92dvh", display:"flex", flexDirection:"column",
        boxShadow:"0 -8px 40px rgba(0,0,0,0.2)", zIndex:1,
      }}>
        {/* Header */}
        <div style={{ background:`linear-gradient(135deg,${C.navy1},${C.navy2})`, borderRadius:"28px 28px 0 0", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <button onClick={onClose} style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.15)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <IconClose size={18} color="#fff" />
          </button>
          <span style={{ color:"#fff", fontSize:17, fontWeight:700 }}>{title || "Select Date"}</span>
          <button onClick={confirm} style={{ background:C.cyan, border:"none", cursor:"pointer", borderRadius:20, padding:"6px 16px", color:"#fff", fontSize:14, fontWeight:700 }}>Done</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY:"auto", padding:"0 20px 40px", flex:1 }}>
          {/* Selected banner */}
          <div style={{ display:"flex", alignItems:"center", gap:12, background:C.cyanBg, borderLeft:`4px solid ${C.cyan}`, borderRadius:12, padding:"12px 14px", margin:"16px 0 20px" }}>
            <IconCalendar size={20} color={C.cyan} />
            <div>
              <p style={{ fontSize:11, fontWeight:700, color:C.text4, textTransform:"uppercase", letterSpacing:0.6, marginBottom:2 }}>Selected date</p>
              <p style={{ fontSize:15, fontWeight:800, color:C.cyanD }}>{pickedLabel}</p>
            </div>
          </div>

          {/* Month nav */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()-1, 1))} style={{ width:38, height:38, borderRadius:10, background:C.cyanBg, border:`1px solid ${C.cyanBorder}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <IconChevL size={20} color={C.cyan} />
            </button>
            <span style={{ fontSize:17, fontWeight:800, color:C.text1 }}>{monthLabel}</span>
            <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth()+1, 1))} style={{ width:38, height:38, borderRadius:10, background:C.cyanBg, border:`1px solid ${C.cyanBorder}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <IconChevR size={20} color={C.cyan} />
            </button>
          </div>

          {/* Weekday labels */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:10 }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <div key={d} style={{ textAlign:"center", fontSize:12, fontWeight:700, color:C.text4, padding:"4px 0" }}>{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:"4px 0", marginBottom:24 }}>
            {grid.map((date, i) => {
              const sel = isSel(date), tod = isToday(date);
              return (
                <button key={i} onClick={() => date && setPicked(date)} disabled={!date}
                  style={{
                    aspectRatio:"1", borderRadius:10, border: tod && !sel ? `1.5px solid ${C.cyan}` : "none",
                    background: sel ? C.cyan : tod ? C.bg : "transparent",
                    cursor: date ? "pointer" : "default", display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:14, fontWeight: sel || tod ? 800 : 600,
                    color: sel ? "#fff" : tod ? C.cyan : date ? C.text1 : "transparent",
                  }}>
                  {date ? date.getDate() : ""}
                </button>
              );
            })}
          </div>

          {/* Confirm button */}
          <button onClick={confirm} style={{ width:"100%", background:`linear-gradient(90deg,${C.cyan},${C.cyanD})`, border:"none", borderRadius:14, padding:"15px 0", display:"flex", alignItems:"center", justifyContent:"center", gap:8, cursor:"pointer", fontSize:16, fontWeight:700, color:"#fff" }}>
            <IconCheck size={18} color="#fff" /> Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Leave Card ────────────────────────────────────────────────────────
function LeaveCard({ item }) {
  const statusKey = (item.status || "Pending");
  const cfg = {
    Pending:  { bg:C.amberBg, accent:C.amber, icon:"clock",   label:C.amber  },
    Approved: { bg:C.greenBg, accent:C.green, icon:"check",   label:C.green  },
    Rejected: { bg:C.redBg,   accent:C.red,   icon:"x-circle",label:C.red    },
  }[statusKey] || { bg:C.amberBg, accent:C.amber, icon:"clock", label:C.amber };

  const from = new Date(item.fromDate + "T00:00:00");
  const to   = new Date(item.toDate   + "T00:00:00");
  const days = Math.ceil((to - from) / 864e5) + 1;
  const fmt  = (d) => d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });

  const StatusIcon = () => {
    if (cfg.icon === "check") return <IconCheck size={22} color={cfg.accent} />;
    if (cfg.icon === "x-circle") return <IconX size={22} color={cfg.accent} />;
    return <IconClock size={22} color={cfg.accent} />;
  };

  return (
    <div style={{ background:C.card, borderRadius:18, borderLeft:`4px solid ${cfg.accent}`, padding:16, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
      {/* Top row */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
        <div style={{ width:46, height:46, borderRadius:12, background:cfg.accent+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <StatusIcon />
        </div>
        <div style={{ flex:1 }}>
          <p style={{ fontSize:14, fontWeight:700, color:C.text1, marginBottom:2 }}>{item.leaveType || "Leave Request"}</p>
          <p style={{ fontSize:12, fontWeight:600, color:cfg.accent }}>{statusKey}</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:5, background:cfg.bg, padding:"4px 10px", borderRadius:20 }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:cfg.accent }} />
          <span style={{ fontSize:12, fontWeight:800, color:cfg.label }}>{days}d</span>
        </div>
      </div>

      {/* Date row */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 0", borderTop:"1px solid rgba(0,0,0,0.06)", borderBottom:"1px solid rgba(0,0,0,0.06)", marginBottom:12 }}>
        <div>
          <p style={{ fontSize:10, fontWeight:800, color:C.text4, letterSpacing:0.8, marginBottom:4 }}>FROM</p>
          <p style={{ fontSize:13, fontWeight:700, color:C.text1 }}>{fmt(from)}</p>
        </div>
        <div style={{ color:C.text4, fontSize:18 }}>→</div>
        <div style={{ textAlign:"right" }}>
          <p style={{ fontSize:10, fontWeight:800, color:C.text4, letterSpacing:0.8, marginBottom:4 }}>TO</p>
          <p style={{ fontSize:13, fontWeight:700, color:C.text1 }}>{fmt(to)}</p>
        </div>
      </div>

      {/* Reason */}
      {item.reason && (
        <div style={{ background:C.bg, borderRadius:10, padding:"10px 12px" }}>
          <p style={{ fontSize:10, fontWeight:800, color:C.text4, letterSpacing:0.8, textTransform:"uppercase", marginBottom:4 }}>Reason</p>
          <p style={{ fontSize:13, color:C.text2, lineHeight:1.5 }}>{item.reason}</p>
        </div>
      )}
    </div>
  );
}

// ─── Leave Form Modal (bottom sheet) ──────────────────────────────────
function LeaveFormModal({ visible, onClose, onSubmit, loading }) {
  const [fromDate, setFromDate] = useState("");
  const [toDate,   setToDate]   = useState("");
  const [reason,   setReason]   = useState("");
  const [pickFrom, setPickFrom] = useState(false);
  const [pickTo,   setPickTo]   = useState(false);
  const [err,      setErr]      = useState("");

  const days = (() => {
    if (!fromDate || !toDate) return 0;
    const d = Math.ceil((new Date(toDate+"T00:00:00") - new Date(fromDate+"T00:00:00")) / 864e5) + 1;
    return d > 0 ? d : 0;
  })();

  const fmtDisplay = (iso) => iso ? new Date(iso+"T00:00:00").toLocaleDateString("en-US", { weekday:"short", month:"long", day:"numeric", year:"numeric" }) : null;

  const reset = () => { setFromDate(""); setToDate(""); setReason(""); setErr(""); };

  const handleClose = () => { reset(); onClose(); };

  const submit = async () => {
    setErr("");
    if (!fromDate || !toDate || !reason.trim()) { setErr("Please fill in all fields."); return; }
    if (new Date(toDate+"T00:00:00") < new Date(fromDate+"T00:00:00")) { setErr("End date must be on or after start date."); return; }
    await onSubmit({ fromDate, toDate, reason: reason.trim() });
    reset();
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={handleClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:200 }} />

      {/* Sheet */}
      <div style={{
        position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)",
        width:"100%", maxWidth:480,
        background:"#fff", borderRadius:"28px 28px 0 0",
        maxHeight:"90dvh", display:"flex", flexDirection:"column",
        boxShadow:"0 -8px 40px rgba(0,0,0,0.2)", zIndex:201,
      }}>
        {/* Handle */}
        <div style={{ width:40, height:4, borderRadius:2, background:C.border, margin:"12px auto 0" }} />

        {/* Header */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", padding:"14px 20px", borderBottom:`1px solid ${C.border}` }}>
          <div>
            <p style={{ fontSize:18, fontWeight:800, color:C.text1 }}>New Leave Request</p>
            <p style={{ fontSize:12, color:C.text4, marginTop:2 }}>Fill in the details below</p>
          </div>
          <button onClick={handleClose} style={{ width:32, height:32, borderRadius:16, background:C.bg, border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <IconClose size={18} color={C.text2} />
          </button>
        </div>

        {/* Scrollable form */}
        <div style={{ overflowY:"auto", padding:"20px 20px 40px", flex:1 }}>

          {/* From Date */}
          <p style={{ fontSize:13, fontWeight:700, color:C.text1, marginBottom:8, display:"flex", alignItems:"center", gap:6 }}>
            <IconCalendar size={14} color={C.cyan} /> From Date
          </p>
          <button onClick={() => setPickFrom(true)} style={{
            width:"100%", display:"flex", alignItems:"center", gap:10,
            border:`1.5px solid ${fromDate ? C.cyan : C.border}`,
            borderRadius:14, padding:"13px 14px",
            background: fromDate ? C.cyanBg : C.bg,
            cursor:"pointer", marginBottom:18, textAlign:"left",
          }}>
            <IconCalendar size={18} color={fromDate ? C.cyan : C.text4} />
            <span style={{ flex:1, fontSize:14, fontWeight:fromDate?500:400, color:fromDate?C.text1:C.text4 }}>
              {fmtDisplay(fromDate) || "Tap to choose start date"}
            </span>
            {fromDate
              ? <span onClick={(e) => { e.stopPropagation(); setFromDate(""); }} style={{ color:C.text4, cursor:"pointer", display:"flex" }}><IconClose size={16} color={C.text4} /></span>
              : <IconChevR size={16} color={C.text4} />
            }
          </button>

          {/* To Date */}
          <p style={{ fontSize:13, fontWeight:700, color:C.text1, marginBottom:8, display:"flex", alignItems:"center", gap:6 }}>
            <IconCalendar size={14} color={C.cyan} /> To Date
          </p>
          <button onClick={() => setPickTo(true)} style={{
            width:"100%", display:"flex", alignItems:"center", gap:10,
            border:`1.5px solid ${toDate ? C.cyan : C.border}`,
            borderRadius:14, padding:"13px 14px",
            background: toDate ? C.cyanBg : C.bg,
            cursor:"pointer", marginBottom: days > 0 ? 12 : 18, textAlign:"left",
          }}>
            <IconCalendar size={18} color={toDate ? C.cyan : C.text4} />
            <span style={{ flex:1, fontSize:14, fontWeight:toDate?500:400, color:toDate?C.text1:C.text4 }}>
              {fmtDisplay(toDate) || "Tap to choose end date"}
            </span>
            {toDate
              ? <span onClick={(e) => { e.stopPropagation(); setToDate(""); }} style={{ color:C.text4, cursor:"pointer", display:"flex" }}><IconClose size={16} color={C.text4} /></span>
              : <IconChevR size={16} color={C.text4} />
            }
          </button>

          {/* Days preview */}
          {days > 0 && (
            <div style={{ display:"flex", alignItems:"center", gap:8, background:C.cyanBg, borderLeft:`3px solid ${C.cyan}`, borderRadius:10, padding:"10px 12px", marginBottom:18 }}>
              <IconInfo size={16} color={C.cyan} />
              <span style={{ fontSize:13, color:C.cyanD, fontWeight:600 }}>
                <strong style={{ fontSize:16, color:C.cyan }}>{days}</strong>
                {" day" + (days !== 1 ? "s" : "") + " selected"}
              </span>
            </div>
          )}

          {/* Reason */}
          <p style={{ fontSize:13, fontWeight:700, color:C.text1, marginBottom:8, display:"flex", alignItems:"center", gap:6 }}>
            <IconSend size={14} color={C.cyan} /> Reason
          </p>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Describe the reason for your leave..."
            rows={4}
            style={{
              width:"100%", border:`1.5px solid ${C.border}`, borderRadius:14,
              padding:"12px 14px", fontSize:14, color:C.text1, background:C.bg,
              fontFamily:"inherit", resize:"vertical", outline:"none",
              boxSizing:"border-box", lineHeight:1.5,
            }}
            onFocus={e => { e.target.style.borderColor = C.cyan; e.target.style.background = C.cyanBg; }}
            onBlur={e  => { e.target.style.borderColor = C.border; e.target.style.background = C.bg; }}
          />

          {/* Error */}
          {err && (
            <div style={{ display:"flex", alignItems:"center", gap:8, background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10, padding:"9px 12px", marginTop:10 }}>
              <IconClose size={14} color={C.red} />
              <span style={{ fontSize:12, fontWeight:600, color:C.red }}>{err}</span>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={submit} disabled={loading}
            style={{
              width:"100%", marginTop:22,
              background:`linear-gradient(90deg,${C.navy1},${C.navy2})`,
              border:"none", borderRadius:14, padding:"15px 0",
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.65 : 1,
              fontSize:16, fontWeight:700, color:"#fff", fontFamily:"inherit",
            }}>
            {loading
              ? <div style={{ width:20, height:20, border:"2.5px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin 0.75s linear infinite" }} />
              : <><IconSend size={18} color="#fff" /> Submit Request</>
            }
          </button>
        </div>
      </div>

      {/* Date pickers rendered above the sheet */}
      <DatePickerModal visible={pickFrom} onClose={() => setPickFrom(false)} onSelect={setFromDate} currentDate={fromDate} title="Select Start Date" />
      <DatePickerModal visible={pickTo}   onClose={() => setPickTo(false)}   onSelect={setToDate}   currentDate={toDate}   title="Select End Date"   />
    </>
  );
}

// ─── Empty State ───────────────────────────────────────────────────────
function EmptyState({ onRequest }) {
  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 40px 20px" }}>
      <div style={{ width:110, height:110, borderRadius:55, background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:20, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
        <IconEmpty size={60} color={C.border} />
      </div>
      <p style={{ fontSize:18, fontWeight:800, color:C.text1, marginBottom:8, textAlign:"center" }}>No Leave Requests</p>
      <p style={{ fontSize:14, color:C.text4, textAlign:"center", marginBottom:24, lineHeight:1.5 }}>Submit your first request and track it here.</p>
      <button onClick={onRequest} style={{ background:`linear-gradient(90deg,${C.cyan},${C.cyanD})`, border:"none", borderRadius:14, padding:"12px 28px", display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:15, fontWeight:700, color:"#fff", fontFamily:"inherit" }}>
        <IconPlus size={18} color="#fff" /> Request Leave
      </button>
    </div>
  );
}

// ─── Main Screen ───────────────────────────────────────────────────────
export default function LeaveRequest() {
  const navigate = useNavigate();
  const [leaves,     setLeaves]     = useState([]);
  const [showForm,   setShowForm]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pageLoad,   setPageLoad]   = useState(true);

  useEffect(() => { fetchLeaves(); }, []);

  const fetchLeaves = useCallback(async () => {
    setRefreshing(true);
    try {
      // GET /leave/my  →  matches getMyLeavesApi()
      const res = await API.get("/leave/my");
      if (res.data.success && Array.isArray(res.data.data)) {
        const sorted = [...res.data.data].sort(
          (a, b) => new Date(b.fromDate+"T00:00:00") - new Date(a.fromDate+"T00:00:00")
        );
        setLeaves(sorted);
      }
    } catch {}
    finally { setRefreshing(false); setPageLoad(false); }
  }, []);

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      // POST /leave/request  →  matches requestLeaveApi()
      const res = await API.post("/leave/request", data);
      if (res.data.success) {
        setShowForm(false);
        fetchLeaves();
      } else {
        alert(res.data.message || "Could not submit request.");
      }
    } catch (e) {
      alert(e.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const total    = leaves.length;
  const approved = leaves.filter(l => l.status === "Approved").length;
  const pending  = leaves.filter(l => l.status === "Pending").length;
  const rejected = leaves.filter(l => l.status === "Rejected").length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        body{background:#0F172A;font-family:'Plus Jakarta Sans',sans-serif}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        .lr-frame{min-height:100dvh;display:flex;align-items:flex-start;justify-content:center;background:#0F172A;padding:0}
        .lr-phone{width:100%;max-width:480px;min-height:100dvh;display:flex;flex-direction:column}
        @media(min-width:520px){
          .lr-frame{padding:24px;align-items:center}
          .lr-phone{min-height:auto;border-radius:40px;overflow:hidden;box-shadow:0 30px 80px rgba(0,0,0,.6)}
        }
        .lr-header{background:linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#334155 100%);padding:14px 20px 20px;flex-shrink:0}
        .lr-body{flex:1;background:#F8FAFC;overflow-y:auto;display:flex;flex-direction:column}
        .card-anim{animation:fadeUp 0.35s ease both}
        .leave-list{display:flex;flex-direction:column;gap:14px;padding:0 20px 32px}
        .stat-divider{width:1px;background:rgba(255,255,255,0.15);margin:4px 0;align-self:stretch}
        button:focus{outline:none}
        textarea:focus{outline:none}
        ::-webkit-scrollbar{width:0;height:0}
      `}</style>

      <div className="lr-frame">
        <div className="lr-phone">

          {/* ══ HEADER ══ */}
          <div className="lr-header">
            {/* Nav row */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <button onClick={() => navigate("/dashboard")} style={{ width:40, height:40, borderRadius:12, background:"rgba(255,255,255,0.1)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <IconBack size={22} color="#fff" />
              </button>
              <span style={{ color:"#fff", fontSize:20, fontWeight:700, letterSpacing:0.3 }}>Leave Requests</span>
              <div style={{ width:40 }} />
            </div>

            {/* Stats bar */}
            <div style={{ display:"flex", background:"rgba(255,255,255,0.08)", borderRadius:16, paddingTop:14, paddingBottom:14 }}>
              {[
                { label:"Total",    val:total,    color:"#fff"  },
                { label:"Approved", val:approved, color:C.green },
                { label:"Pending",  val:pending,  color:C.amber },
                { label:"Rejected", val:rejected, color:C.red   },
              ].map((s, i, arr) => (
                <React.Fragment key={s.label}>
                  <div style={{ flex:1, textAlign:"center" }}>
                    <p style={{ color:s.color, fontSize:20, fontWeight:800, lineHeight:1 }}>{pageLoad ? "–" : s.val}</p>
                    <p style={{ color:"rgba(255,255,255,0.6)", fontSize:11, fontWeight:600, marginTop:3 }}>{s.label}</p>
                  </div>
                  {i < arr.length - 1 && <div className="stat-divider" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ══ BODY ══ */}
          <div className="lr-body">
            {/* Request button */}
            <div style={{ padding:"20px 20px 4px" }}>
              <button
                onClick={() => setShowForm(true)}
                style={{ width:"100%", background:`linear-gradient(90deg,${C.cyan},${C.cyanD})`, border:"none", borderRadius:14, padding:"15px 0", display:"flex", alignItems:"center", justifyContent:"center", gap:10, cursor:"pointer", fontFamily:"inherit", fontSize:16, fontWeight:700, color:"#fff", boxShadow:`0 4px 14px ${C.cyan}55` }}
              >
                <IconPlus size={20} color="#fff" /> Request New Leave
              </button>
            </div>

            {/* Loading spinner */}
            {pageLoad && (
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:40 }}>
                <div style={{ width:36, height:36, border:"3px solid rgba(6,182,212,0.2)", borderTopColor:C.cyan, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
              </div>
            )}

            {/* Pull-to-refresh indicator */}
            {refreshing && !pageLoad && (
              <div style={{ display:"flex", justifyContent:"center", padding:"12px 0 0" }}>
                <div style={{ width:20, height:20, border:`2.5px solid rgba(6,182,212,0.2)`, borderTopColor:C.cyan, borderRadius:"50%", animation:"spin 0.75s linear infinite" }} />
              </div>
            )}

            {/* Empty state */}
            {!pageLoad && leaves.length === 0 && (
              <EmptyState onRequest={() => setShowForm(true)} />
            )}

            {/* Section label */}
            {!pageLoad && leaves.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:8, margin:"20px 20px 12px" }}>
                <div style={{ width:4, height:18, borderRadius:2, background:C.cyan }} />
                <span style={{ fontSize:15, fontWeight:800, color:C.text1 }}>Your Requests</span>
                <span style={{ marginLeft:"auto", fontSize:12, color:C.text3, fontWeight:500 }}>
                  {total} total
                </span>
              </div>
            )}

            {/* Leave cards list */}
            {!pageLoad && leaves.length > 0 && (
              <div className="leave-list">
                {leaves.map((item, i) => (
                  <div key={item._id} className="card-anim" style={{ animationDelay: `${i * 0.05}s` }}>
                    <LeaveCard item={item} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form modal */}
      <LeaveFormModal
        visible={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </>
  );
}